import type * as React from 'react';
import { t } from '@reos/i18n';
import type { PreparingSectionCopy } from '../lib/texts.js';
import { PREPARING_BADGE, PREPARING_SUBTITLE, PREPARING_WHAT_HEADING } from '../lib/texts.js';

/**
 * Krok UX1 (AUD1 §3.2): skupina „brzy/beta“ nese JEDNOTNÝ štítek „Obchod brzy“ —
 * makléř tak hned vidí, že je to plánovaná fáze, ne polovičitá funkce.
 */
export const PREPARING_SOON_BADGE = t('webAdmin.preparing.soonBadge', { locale: 'cs' });

const DESIGN_PREVIEW_LABEL = t('webAdmin.preparingSection.designPreviewLabel', { locale: 'cs' });
const DESIGN_PREVIEW_NOTE = t('webAdmin.preparingSection.designPreviewNote', { locale: 'cs' });
const DESIGN_PREVIEW_WATERMARK = t('webAdmin.preparingSection.designPreviewWatermark', { locale: 'cs' });

/**
 * Nahled grafickeho navrhu (BLOK 7 CAST A): obrazek neinteraktivni
 * (pointer-events:none, aria-hidden), ztlumeny a s vodoznakem NAVRH. Klik na
 * ramecek otevre obrazek v plne velikosti (novy tab) — samotny <img> klik
 * nezachytava, protoze je pointer-events:none.
 */
function DesignPreview({ src, title }: { readonly src: string; readonly title: string }): React.JSX.Element {
  return (
    <div className="mt-4" style={{ maxWidth: 720 }}>
      <p className="m-0 mb-2 text-sm font-semibold">{DESIGN_PREVIEW_LABEL}</p>
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        aria-label={t('webAdmin.preparingSection.openFullSizeAriaLabel', { locale: 'cs', values: { label: DESIGN_PREVIEW_LABEL, title } })}
        style={{ position: 'relative', display: 'block', borderRadius: 'var(--rh-radius-card)', overflow: 'hidden', border: '1px solid var(--rh-border)' }}
      >
        <img
          src={src}
          alt={t('webAdmin.preparingSection.imageAlt', { locale: 'cs', values: { title } })}
          aria-hidden="true"
          style={{ display: 'block', width: '100%', height: 'auto', opacity: 0.92, pointerEvents: 'none' }}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            padding: '4px 10px',
            fontSize: 'var(--rh-size-meta)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            borderRadius: 999,
            color: 'var(--rh-card)',
            background: 'var(--rh-warning)',
            boxShadow: '0 1px 3px var(--rh-shadow-menu)',
          }}
        >
          {DESIGN_PREVIEW_WATERMARK}
        </span>
      </a>
      <p className="mt-2 text-sm" style={{ color: 'var(--rh-text-muted)' }}>
        {DESIGN_PREVIEW_NOTE}
      </p>
    </div>
  );
}

/**
 * Jednotny stav nehotove sekce (zadani BLOK 2, ukol 2): badge PRIPRAVUJE SE
 * + konkretni popis, co v sekci bude. Nikde se nepise „neni hotova" a zadna
 * sekce neni prazdna stranka. BLOK 7 CAST A: sekce s existujicim graf.
 * navrhem navic ukazuje jeho neinteraktivni nahled (copy.designImage).
 */
export function PreparingSection({
  copy,
  compact = false,
  badge = PREPARING_BADGE,
}: {
  readonly copy: PreparingSectionCopy;
  /** Kompaktni varianta pro zalozku detailu (bez H1, jen obsah karty). */
  readonly compact?: boolean;
  /** Krok UX1: skupina „brzy“ nese „Obchod brzy“, zbytek „PŘIPRAVUJE SE“. */
  readonly badge?: string;
}): React.JSX.Element {
  return (
    <div data-testid="preparing-section">
      {!compact && (
        <header className="flex flex-wrap items-center gap-3 mb-4">
          <h1 className="rh-overview__title">{copy.title}</h1>
          <span className="rh-badge rh-badge--warning">{badge}</span>
        </header>
      )}
      <div className={compact ? '' : 'rh-card'} style={{ maxWidth: 720 }}>
        {compact && (
          <p className="m-0 mb-2">
            <span className="rh-badge rh-badge--warning">{badge}</span>
          </p>
        )}
        <p className="m-0 text-sm font-medium" style={{ color: 'var(--rh-text-muted)' }}>
          {PREPARING_SUBTITLE}
        </p>
        <p className="mt-2 mb-3" style={{ lineHeight: 1.6 }}>
          {copy.perex}
        </p>
        <p className="m-0 mb-1 text-sm font-semibold">{PREPARING_WHAT_HEADING}:</p>
        <ul className="m-0 pl-5 text-sm" style={{ lineHeight: 1.7, listStyle: 'disc' }}>
          {copy.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
      {copy.designImage !== undefined ? <DesignPreview src={copy.designImage} title={copy.title} /> : null}
    </div>
  );
}
